import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cloneDeep } from 'lodash'
import { EllipsisVertical, Loader2 } from 'lucide-react'
import {
  awaitPendingMessage,
  sendTestMessage,
} from 'app/frontend/base/api/notification'
import { toast } from 'sonner'
import { useState } from 'react'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTranslation } from 'react-i18next'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useQueryClient } from '@tanstack/react-query'

type Props = {
  template: Partial<wowaudit.NotificationTemplate>
  setOpenTemplate: (template: Partial<wowaudit.NotificationTemplate>) => void
  setOpenWarningModal?: (templateId: number) => void
}

const TemplateContextMenu = ({
  template,
  setOpenTemplate,
  setOpenWarningModal,
}: Props) => {
  const { team } = useTeam()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [testMessageLoading, setTestMessageLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const completeTestMessage = () => {
    setTestMessageLoading(false)
    setOpen(false)
    queryClient.invalidateQueries({
      queryKey: ['notifications', { id: team.id }],
    })
  }

  const triggerTestMessage = async (
    template: Partial<wowaudit.NotificationTemplate>,
  ) => {
    if (!template.id) return
    setTestMessageLoading(true)

    try {
      const result = await sendTestMessage(template.id, team.id)

      if (result.notification_id) {
        awaitPendingMessage(team.id, result.notification_id, (notification) => {
          const { deliveryStatus, errorStatus } = notification

          if (deliveryStatus === 'delivered') {
            toast.success('Test message sent successfully')
          } else {
            toast.error(
              `Failed to deliver test message. ${errorStatus} (${deliveryStatus})`,
            )
          }

          completeTestMessage()
        })
      } else {
        toast.error('Failed to schedule test message')
        completeTestMessage()
      }
    } catch (error) {
      displayErrorToast(error as Error, t)
      completeTestMessage()
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger>
        <div className='flex items-center justify-center cursor-pointer h-5'>
          <EllipsisVertical size={18} />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' alignOffset={-10}>
        <DropdownMenuItem onClick={() => setOpenTemplate(cloneDeep(template))}>
          {t('notifications.edit-template')}
        </DropdownMenuItem>

        {setOpenWarningModal && template.id ? (
          <DropdownMenuItem
            onClick={() => setOpenWarningModal(template.id as number)}
          >
            {t('notifications.show-warnings')}
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuItem
          disabled={testMessageLoading}
          onClick={(e) => {
            e.preventDefault()
            if (testMessageLoading) return
            triggerTestMessage(template)
          }}
          className='w-36'
        >
          {testMessageLoading ? (
            <span className='flex items-center gap-2'>
              <Loader2 className='w-4 h-4 animate-spin' />
              {t('notifications.sending-test-message')}
            </span>
          ) : (
            t('notifications.send-test-message')
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default TemplateContextMenu
